/* Body Collective free gift, built into the theme.
   Rule: once the cart total (after discounts) reaches the threshold, the cart holds exactly one gift; under it, none.

   Speed: a cart write is the slow part of any cart action, so the gift travels inside the shopper's own write
   whenever the outcome can be predicted (the price being added is known). The drawer then opens once, at its
   normal speed, with the gift already in it. When the outcome cannot be predicted, the cart is checked straight
   after the write and corrected before the drawer is drawn. Nothing here can block or break a cart action:
   on any error the original response is handed back untouched. */
(() => {
  const cfg = window.__bcGift;
  if (!cfg || !cfg.enabled || !cfg.variantId || window.__bcGiftReady) return;
  window.__bcGiftReady = true;

  const GIFT = Number(cfg.variantId);
  const LIMIT = Number(cfg.threshold);
  const UP = 1.2;    // bundle the gift only when the predicted total clears the threshold by this margin (discounts shrink totals)
  const DOWN = 0.9;  // bundle its removal only when the predicted total is this far under
  const STATE = 'bc-cart-state';
  const DRAWER = ['mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble'];
  const native = window.fetch.bind(window);
  const WRITE = /\/cart\/(add|change|update|clear)(?:\.js)?(?:\?|$)/;
  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

  let known = normalise(cfg.cart);
  let queue = Promise.resolve();
  const prices = new Map();

  function normalise(cart) {
    const items = (cart.items || []).map((i) => ({
      key: i.key, variant_id: Number(i.variant_id), quantity: i.quantity,
      final_line_price: i.final_line_price, price: i.original_price != null ? i.original_price : i.price
    }));
    return { total: cart.total_price != null ? cart.total_price : cart.total, items };
  }
  const remember = (state) => { known = state; state.items.forEach((i) => prices.set(i.variant_id, i.price)); };
  remember(known);

  const giftLine = (s) => s.items.find((i) => i.variant_id === GIFT);
  const hasOthers = (s) => s.items.some((i) => i.variant_id !== GIFT);
  const decide = (s) => {
    const gift = giftLine(s);
    const earns = s.total >= LIMIT && hasOthers(s);
    if (earns && !gift) return { op: 'add' };
    if (!earns && gift) return { op: 'remove', key: gift.key };
    if (earns && gift.quantity > 1) return { op: 'one', key: gift.key };
    return null;
  };

  const priceOf = (id) => {
    id = Number(id);
    if (prices.has(id)) return prices.get(id);
    try {
      const meta = (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) || {};
      const lists = [].concat(meta.product ? [meta.product] : [], meta.products || []);
      lists.forEach((p) => (p.variants || []).forEach((v) => prices.set(Number(v.id), Number(v.price))));
      document.querySelectorAll('script[data-bc-prices]').forEach((el) => {
        const map = JSON.parse(el.textContent);
        Object.keys(map).forEach((k) => { if (Number(k)) prices.set(Number(k), Number(map[k])); });
      });
    } catch (e) { /* a missing hint only means the slower, checked path */ }
    return prices.has(id) ? prices.get(id) : null;
  };

  const stateFromSections = (sections) => {
    const html = sections && sections[STATE];
    if (!html) return null;
    const m = html.match(/<script[^>]*data-bc-cart-state[^>]*>([\s\S]*?)<\/script>/);
    try { return m ? normalise(JSON.parse(m[1])) : null; } catch (e) { return null; }
  };
  const readCart = () => native('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }).then((r) => r.json()).then(normalise);

  const post = (url, body) => native(url, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  const respond = (res, payload) => {
    const headers = new Headers(res.headers); headers.delete('content-length'); headers.delete('content-encoding');
    return new Response(JSON.stringify(payload), { status: res.status, statusText: res.statusText, headers });
  };
  const withoutState = (sections) => {
    if (!sections) return sections;
    const out = Object.assign({}, sections); delete out[STATE]; return out;
  };

  // A second write, used only when the first could not carry the gift change.
  const correct = async (action, sections, sectionsUrl) => {
    const list = (sections || []).filter((id) => id !== STATE);
    const body = action.op === 'add' ? { items: [{ id: GIFT, quantity: 1 }] } : { id: action.key, quantity: action.op === 'remove' ? 0 : 1 };
    if (list.length) { body.sections = list; body.sections_url = sectionsUrl || window.location.pathname; }
    const res = await post(action.op === 'add' ? '/cart/add.js' : '/cart/change.js', body);
    if (!res.ok) throw new Error('gift correction failed');
    const data = await res.json();
    if (action.op === 'add') known.items.unshift({ key: '', variant_id: GIFT, quantity: 1, final_line_price: 0, price: 0 });
    else if (action.op === 'remove') known.items = known.items.filter((i) => i.variant_id !== GIFT);
    else { const g = giftLine(known); if (g) g.quantity = 1; }
    if (data.items && typeof data.total_price === 'number') remember(normalise(data));
    return data.sections;
  };

  const paintDrawer = (sections) => {
    Object.keys(sections || {}).forEach((id) => {
      const el = document.getElementById(id);
      if (!el || !sections[id]) return;
      const fresh = new DOMParser().parseFromString(sections[id], 'text/html').querySelector('.shopify-section');
      if (fresh) el.innerHTML = fresh.innerHTML;
    });
  };

  // For cart changes that draw nothing themselves: the page as it loaded, an app, an old style request.
  const reconcile = async (state) => {
    remember(state || await readCart());
    const action = decide(known);
    if (action) paintDrawer(await correct(action, DRAWER));
  };

  const readBody = (init) => {
    const body = init && init.body;
    try {
      if (typeof body === 'string') return { kind: 'json', data: JSON.parse(body) };
      if (typeof FormData !== 'undefined' && body instanceof FormData) return { kind: 'form', data: body };
    } catch (e) { /* fall through */ }
    return null;
  };
  const listOf = (v) => (Array.isArray(v) ? v : String(v || '').split(',')).filter(Boolean);

  async function handleAdd(input, init, ctx) {
    const parsed = readBody(init);
    if (!parsed) return checked(input, init, null, null, ctx);

    let items, sections, sectionsUrl, files = false;
    if (parsed.kind === 'json') {
      const d = parsed.data;
      items = Array.isArray(d.items) ? d.items : [{ id: d.id, quantity: d.quantity || 1, properties: d.properties, selling_plan: d.selling_plan }];
      sections = listOf(d.sections); sectionsUrl = d.sections_url;
    } else {
      const f = parsed.data;
      if ([...f.keys()].some((k) => k.indexOf('items[') === 0)) return checked(input, init, null, null, ctx);
      const props = {};
      for (const [k, v] of f.entries()) {
        const m = k.match(/^properties\[(.+)\]$/);
        if (m) { if (typeof v !== 'string') files = files || (v && v.size > 0); else if (v !== '') props[m[1]] = v; }
      }
      const item = { id: f.get('id'), quantity: Number(f.get('quantity')) || 1 };
      if (Object.keys(props).length) item.properties = props;
      if (f.get('selling_plan')) item.selling_plan = f.get('selling_plan');
      items = [item]; sections = listOf(f.get('sections')); sectionsUrl = f.get('sections_url');
    }
    if (!items.length || items.some((i) => Number(i.id) === GIFT)) { ctx.sent = true; return native(input, init); }

    const single = !(parsed.kind === 'json' && Array.isArray(parsed.data.items));
    const added = items.reduce((sum, i) => { const p = priceOf(i.id); return sum === null || p === null ? null : sum + p * (Number(i.quantity) || 1); }, 0);
    const bundle = !giftLine(known) && !files && added !== null && known.total + added >= LIMIT * UP;
    const wanted = sections.length && sections.length < 5 ? sections.concat(STATE) : sections;

    let request = init;
    if (bundle) {
      const body = { items: items.concat({ id: GIFT, quantity: 1 }) };
      if (wanted.length) { body.sections = wanted; body.sections_url = sectionsUrl || window.location.pathname; }
      const headers = new Headers((init && init.headers) || {}); headers.set('Content-Type', 'application/json');
      request = Object.assign({}, init, { headers, body: JSON.stringify(body) });
    } else if (wanted.length !== sections.length) {
      if (parsed.kind === 'json') request = Object.assign({}, init, { body: JSON.stringify(Object.assign({}, parsed.data, { sections: wanted })) });
      else { const copy = new FormData(); for (const [k, v] of parsed.data.entries()) copy.append(k, k === 'sections' ? wanted.join(',') : v); request = Object.assign({}, init, { body: copy }); }
    }

    ctx.sent = true;
    const res = await native(input, request);
    if (!res.ok) return res;
    try {
      const data = await res.clone().json();
      remember(stateFromSections(data.sections) || await readCart());

      let outSections = withoutState(data.sections);
      const action = decide(known);
      if (action) {
        const fixed = await correct(action, sections, sectionsUrl);
        if (sections.length) outSections = fixed || outSections; else paintDrawer(await sectionsFor(DRAWER));
      }
      let payload = data;
      if (bundle && single && Array.isArray(data.items)) payload = data.items.find((i) => Number(i.variant_id) === Number(items[0].id)) || data.items[0];
      payload = Object.assign({}, payload);
      if (outSections) payload.sections = outSections; else delete payload.sections;
      return respond(res, payload);
    } catch (e) {
      return res;
    }
  }

  async function handleChange(input, init, kind, ctx) {
    const parsed = readBody(init);
    if (!parsed || parsed.kind !== 'json') return checked(input, init, null, null, ctx);
    const d = parsed.data;
    const sections = listOf(d.sections);
    let request = init, url = input;

    if (kind === 'change' && d.quantity != null) {
      const line = d.line ? known.items[Number(d.line) - 1] : known.items.find((i) => i.key === d.id || String(i.variant_id) === String(d.id));
      if (line && line.variant_id !== GIFT && line.key) {
        const qty = Number(d.quantity);
        const unit = line.quantity ? line.final_line_price / line.quantity : 0;
        const predicted = known.total - line.final_line_price + unit * qty;
        const othersLeft = known.items.some((i) => i.variant_id !== GIFT && (i !== line || qty > 0));
        const gift = giftLine(known);
        let giftQty = null;
        if (!gift && othersLeft && predicted >= LIMIT * UP) giftQty = 1;
        if (gift && gift.key && (!othersLeft || predicted < LIMIT * DOWN)) giftQty = 0;
        if (giftQty !== null) {
          const updates = {}; updates[line.key] = qty; updates[giftQty ? GIFT : gift.key] = giftQty;
          const body = { updates };
          if (sections.length) { body.sections = sections; body.sections_url = d.sections_url || window.location.pathname; }
          url = '/cart/update.js';
          request = Object.assign({}, init, { body: JSON.stringify(body) });
        }
      }
    }
    return checked(url, request, sections, d.sections_url, ctx);
  }

  // Send the write as given, then make sure the gift matches the cart that came back.
  async function checked(input, init, sections, sectionsUrl, ctx) {
    if (ctx) ctx.sent = true;
    const res = await native(input, init);
    if (!res.ok) return res;
    try {
      const data = await res.clone().json();
      const full = data && Array.isArray(data.items) && typeof data.total_price === 'number' && 'item_count' in data;
      remember(full ? normalise(data) : (stateFromSections(data.sections) || await readCart()));
      const action = decide(known);
      if (!action) return res;
      const asked = sections || [];
      const fixed = await correct(action, asked, sectionsUrl);
      if (!asked.length) { paintDrawer(await sectionsFor(DRAWER)); return res; }
      const payload = Object.assign({}, data, { sections: fixed || data.sections });
      if (full) payload.item_count = known.items.reduce((n, i) => n + i.quantity, 0);
      return respond(res, payload);
    } catch (e) {
      return res;
    }
  }

  const sectionsFor = (list) => native('/?sections=' + list.join(','), { cache: 'no-store' }).then((r) => r.json()).catch(() => ({}));

  window.fetch = function (input, init) {
    const isText = typeof input === 'string' || (typeof URL !== 'undefined' && input instanceof URL);
    const url = isText ? String(input) : (input && input.url) || '';
    const method = String((init && init.method) || (!isText && input && input.method) || 'GET').toUpperCase();
    const m = method === 'POST' ? url.match(WRITE) : null;
    if (!m) return native(input, init);
    if (!isText) {
      // A prepared Request cannot be reshaped: let it through, then bring the gift into line.
      return native(input, init).then((res) => { queue = queue.then(() => reconcile()).catch(() => {}); return res; });
    }
    const kind = m[1];
    const run = () => {
      const ctx = { sent: false };
      const work = kind === 'add' ? handleAdd(input, init, ctx) : kind === 'clear' ? checked(input, init, null, null, ctx) : handleChange(input, init, kind, ctx);
      // A failure before the write went out falls back to the plain request. After it, never resend.
      return work.catch((err) => { if (ctx.sent) throw err; return native(input, init); });
    };
    const result = queue.then(run, run);
    queue = result.then(() => {}, () => {});
    return result;
  };

  // Cart writes that do not go through fetch (older apps use XMLHttpRequest).
  const open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (String(method).toUpperCase() === 'POST' && WRITE.test(String(url))) {
      this.addEventListener('load', () => { queue = queue.then(() => reconcile()).catch(() => {}); });
    }
    return open.apply(this, arguments);
  };

  if (decide(known)) queue = queue.then(() => reconcile(known)).catch(() => {});
})();
